import axios from '@/plugins/axios'
import { Module } from 'vuex'

// Member Settings > Audience Mapping. Each audience (the framework's built-ins, then the workspace's
// own) has a communication objective and content domains with angles: the member can reword them,
// switch domains off, and writes their approach. The API returns every audience, in order, with the
// framework's wording alongside (objectiveDefault, angleDefault), so a save returns the map as stored.
// draftAudience starts an AI draft of an audience's approach, which runs in the background
// (AudienceDraftRunner); getAudienceDrafts says how each audience's latest one went. The workspace
// audience actions are for admins adding their own audiences, which AI sets up in the background.

export type AudienceKey = string
export type AudienceTone = 'casual' | 'conversational' | 'professional' | 'formal'
export type SetupStatus = 'queued' | 'running' | 'done' | 'failed'

export interface AudienceDomain {
    key: string
    name: string
    active: boolean
    angle: string
    angleDefault: string
}

export interface Audience {
    key: AudienceKey
    name: string
    who: string
    builtIn: boolean
    id: number | null
    setupStatus: SetupStatus
    active: boolean
    objective: string
    objectiveDefault: string
    approach: string
    tone: AudienceTone
    domains: AudienceDomain[]
}

export interface AudienceMapResponse {
    audiences: Audience[]
    // Switched on from the member's role the first time the map was opened
    defaultsApplied: AudienceKey[]
    canManageAudiences: boolean
}

export interface AudienceDraft {
    id: number
    audience: AudienceKey
    status: 'queued' | 'running' | 'done' | 'failed'
    filled: string[]
    error: string | null
}

// A workspace's own audience, as admins edit it
export interface WorkspaceAudience {
    key: AudienceKey
    id: number
    name: string
    who: string
    objective: string
    tone: AudienceTone
    domains: { key?: string; name: string; angle: string }[]
    setupStatus: SetupStatus
}

const fresh = { cache: false }

const audienceMapModule: Module<any, any> = {
    actions: {
        async getAudienceMap(_, memberId: number): Promise<AudienceMapResponse> {
            const { data } = await axios.get(`/api/audience-maps/${memberId}`, fresh)
            return { audiences: data.audiences, defaultsApplied: data.defaultsApplied ?? [], canManageAudiences: !!data.canManageAudiences }
        },

        // Start an AI draft of one audience's approach (or get the one already under way); the API
        // saves what it fills. Silent: the page says what went wrong.
        // mode "angles": Generate, which rewrites the audience's angles for the executive and company
        async draftAudience(_, { memberId, audience, mode }: { memberId: number; audience: AudienceKey; mode?: 'approach' | 'angles' }): Promise<AudienceDraft> {
            const { data } = await axios.post(`/api/audience-maps/${memberId}/draft`, mode ? { audience, mode } : { audience }, { silent: true })
            return data
        },

        // Each audience's latest AI draft, by audience
        async getAudienceDrafts(_, memberId: number): Promise<Partial<Record<AudienceKey, AudienceDraft>>> {
            const { data } = await axios.get(`/api/audience-maps/${memberId}/drafts`, { ...fresh, silent: true })
            return data.drafts ?? {}
        },

        // Each audience's latest Generate
        async getAudienceAngleDrafts(_, memberId: number): Promise<Partial<Record<AudienceKey, AudienceDraft>>> {
            const { data } = await axios.get(`/api/audience-maps/${memberId}/drafts`, { ...fresh, silent: true })
            return data.angles ?? {}
        },

        async updateAudienceMap(_, { memberId, audiences }: { memberId: number; audiences: Audience[] }): Promise<Audience[]> {
            const { data } = await axios.put(`/api/audience-maps/${memberId}`, { audiences })
            return data.audiences
        },

        async getWorkspaceAudiences(): Promise<WorkspaceAudience[]> {
            const { data } = await axios.get('/api/workspace-audiences', { ...fresh, silent: true })
            return data.audiences ?? []
        },

        // Silent: the popup shows the API's reason (e.g. a name that's taken)
        async createWorkspaceAudience(_, { name, who }: { name: string; who: string }): Promise<WorkspaceAudience> {
            const { data } = await axios.post('/api/workspace-audiences', { name, who }, { silent: true })
            return data
        },

        async updateWorkspaceAudience(_, { id, ...changes }: Partial<WorkspaceAudience> & { id: number }): Promise<WorkspaceAudience> {
            const { data } = await axios.put(`/api/workspace-audiences/${id}`, changes, { silent: true })
            return data
        },

        async setUpWorkspaceAudience(_, id: number): Promise<WorkspaceAudience> {
            const { data } = await axios.post(`/api/workspace-audiences/${id}/setup`, {}, { silent: true })
            return data
        },

        async deleteWorkspaceAudience(_, id: number): Promise<void> {
            await axios.delete(`/api/workspace-audiences/${id}`, { silent: true })
        },
    },
}

export default audienceMapModule
